<template>
  <div class="file-viewer h-full">
    <!-- Content override mode: simplified header + custom component -->
    <template v-if="contentOverride">
      <div class="flex h-full flex-col space-y-2">
        <LayoutColumnHeader :class="color">
          <div class="flex w-full min-w-0 items-center justify-between gap-x-2">
            <div class="flex min-w-0 items-center gap-x-2">
              <FontAwesomeIcon :icon="['fas', 'eye']" />
              <span class="truncate">{{ contentOverrideTitle }}</span>
            </div>
            <GeneralButton
              :color="color"
              :icon="['fas', 'xmark']"
              title="Sluiten"
              class="shrink-0"
              @click.stop="clearContentOverride"
            />
          </div>
        </LayoutColumnHeader>

        <div class="min-h-0 flex-1">
          <component
            :is="contentOverride"
            v-bind="contentOverrideProps"
            @loading-complete="emit('loadingComplete')"
          />
        </div>
      </div>
    </template>

    <!-- Normal file viewer mode -->
    <div v-else class="pointer-events-none flex h-full flex-col space-y-2">
      <div
        v-if="modelValue?.length > 0"
        class="pointer-events-auto hidden overflow-hidden rounded-md p-2"
        :class="getDraggableWindow ? 'sm:order-1' : ''"
      >
        <FileBadges
          v-if="selectedFileRelation"
          v-model="modelValue"
          :custom-file-viewer-handler="setFileRelations"
          :selected="selectedFileRelation"
        />
      </div>

      <LayoutColumnHeader
        :show-title="!hideMenu && !getDraggableWindow"
        :show-navigation-menu="!hideMenu"
        class="pointer-events-auto"
        :class="[!!getDraggableWindow ? 'rounded-none!' : '', color]"
      >
        <div
          class="flex w-full items-center justify-evenly space-x-2 overflow-hidden sm:justify-start"
        >
          <GeneralButton
            :color="color"
            :disabled="!modelValue?.length || modelValue.length <= 1"
            :icon="['fas', 'arrow-left']"
            @click.stop="cycleFileRelation('prev')"
            title="Vorig bestand"
          />
          <GeneralButton
            :color="color"
            :disabled="!modelValue?.length || modelValue.length <= 1"
            :icon="['fas', 'arrow-right']"
            @click.stop="cycleFileRelation('next')"
            title="Volgend bestand"
          />
          <div
            v-if="modelValue?.length > 1"
            class="space-x-2 px-2 text-xs select-none"
            :class="color === 'primary-lighten-1' ? 'text-on-primary-lighten-1' : 'text-on-primary'"
          >
            <span>{{ fileIndex + 1 }}</span>
            <span>van</span>
            <span>{{ modelValue.length }}</span>
          </div>
          <GeneralButton
            :color="color"
            :disabled="!selectedFileRelation"
            :icon="['fas', 'download']"
            @click="downloadFile(selectedFileRelation)"
            title="Bestand downloaden"
          />
          <GeneralButton
            v-if="allowDelete && canModifySelectedFile"
            :color="color"
            :disabled="!selectedFileRelation || !!selectedDeleteDisabledReason"
            :icon="['fas', 'trash-can']"
            @click="removeFileRelation"
            class="hover:text-warning"
            title="Bestand verwijderen"
          />
          <GeneralButton
            v-if="canModifySelectedFile"
            :color="color"
            :icon="['fas', 'rotate-right']"
            :disabled="
              !selectedFileRelation?.file.isImage() || modelValue.length === 0 || isCropMode
            "
            @click="selectedFileRelation?.file.isImage() ? rotateFile() : undefined"
            title="Bestand roteren"
          />
          <GeneralButton
            v-if="selectedFileRelation?.file.getDerivedFile() !== null"
            :color="isShowOriginalImage ? 'secondary' : color"
            :icon="['fas', 'file-image']"
            :disabled="
              !selectedFileRelation?.file.isImage() || modelValue.length === 0 || isRotatingFile
            "
            @click="isShowOriginalImage = !isShowOriginalImage"
            :title="isShowOriginalImage ? 'Uitsnede bekijken' : 'Origineel bekijken'"
          />
          <GeneralButton
            v-if="
              canModifySelectedFile &&
              (selectedFileRelation?.file.getDerivedFile() === null || isShowOriginalImage)
            "
            :color="isCropMode ? 'primary' : color"
            :icon="['fas', 'crop']"
            :disabled="
              !selectedFileRelation?.file.isImage() || modelValue.length === 0 || isRotatingFile
            "
            @click="isCropMode = !isCropMode"
            title="Bijsnijden"
          />
          <GeneralButton
            v-if="!hideMenu"
            :disabled="!selectedFileRelation"
            :color="color"
            :icon="['fas', 'file-circle-xmark']"
            @click="deselectFileRelation"
            title="Voorvertoning sluiten"
          />
        </div>
      </LayoutColumnHeader>

      <div
        class="pointer-events-auto relative flex h-full grow justify-center p-2"
        :class="[
          selectedFileRelation?.file.isImage() ? 'overflow-hidden' : 'overflow-auto',
          selectedFileRelation?.file.isDocument() ? 'items-start' : 'items-center',
          selectedFileRelation ? 'surface rounded-md' : ''
        ]"
      >
        <!-- Image rendering block -->
        <div
          v-if="selectedFileRelation?.file.isImage() && !isCropMode"
          ref="imageContainer"
          class="image-container relative overflow-visible! transition-all duration-300"
          @dblclick="rotateFile()"
        >
          <!-- Normal image view -->
          <img
            v-if="selectedFileRelation"
            :key="selectedFileRelation.id"
            ref="imageElement"
            :alt="selectedFileRelation.file.originalName"
            :class="{ grayscale: modelValue.length === 0 }"
            :src="imgUrl"
            :style="{ 'max-height': maxImageHeight }"
            class="h-full w-full rounded-md select-none"
            @load="onContentLoaded"
            @error="onContentFailed"
          />

          <!-- Loader overlay -->
          <div
            v-if="isLoading"
            class="surface absolute inset-0 flex h-full w-full items-center justify-center"
          >
            <LoadingSpinner class="size-6" />
          </div>
        </div>

        <!-- Crop editor -->
        <FileViewerCrop
          v-if="selectedFileRelation?.file.isImage() && isCropMode"
          :image-url="selectedFileRelation.file.url"
          @close="isCropMode = false"
          @complete="onCropComplete"
        />

        <!-- Document rendering block -->
        <div
          v-if="selectedFileRelation?.file.isDocument()"
          class="h-full w-full transition-all duration-300"
        >
          <object
            v-if="showNativeDocumentViewer"
            :key="`native:${documentViewerKey}`"
            :data="nativeDocumentUrl"
            :height="maxImageHeight"
            class="h-full rounded-md"
            frameborder="0"
            :type="nativeDocumentMimeType"
            width="100%"
            @load="onDocumentContentLoaded"
            @error="onNativeDocumentFailed"
          />
          <iframe
            v-else-if="showIframeDocumentViewer"
            :key="`iframe:${documentViewerKey}`"
            :height="maxImageHeight"
            :src="iframeUrl"
            class="h-full rounded-md"
            frameborder="0"
            title="Bestandsvoorbeeld"
            width="100%"
            @load="onIframeDocumentLoaded"
            @error="onIframeDocumentFailed"
          />
          <div
            v-else-if="documentViewerFailed"
            class="flex h-full w-full flex-col items-center justify-center space-y-4 text-center select-none"
          >
            <FontAwesomeIcon :icon="['fas', 'file']" size="3x" />
            <div class="space-y-1">
              <div class="font-bold">Voorvertoning kan niet worden geladen</div>
              <div class="text-2xs max-w-full truncate break-all">
                {{ selectedFileRelation.file.originalName }}
              </div>
            </div>
            <div class="flex items-center justify-center space-x-2">
              <GeneralButton
                size="small"
                variant="outlined"
                :color="color"
                :icon="['fas', 'rotate-right']"
                title="Bestand opnieuw laden"
                @click="reloadFileRelation"
              >
                Opnieuw proberen
              </GeneralButton>
              <GeneralButton
                size="small"
                variant="outlined"
                :color="color"
                :icon="['fas', 'download']"
                title="Bestand downloaden"
                @click="downloadFile(selectedFileRelation)"
              >
                Downloaden
              </GeneralButton>
            </div>
          </div>
        </div>

        <!-- Fallback for other file types -->
        <div
          v-if="
            selectedFileRelation &&
            !selectedFileRelation?.file.isDocument() &&
            !selectedFileRelation?.file.isImage()
          "
          class="w-full text-center select-none"
        >
          <div class="mx-auto max-w-96 space-y-2">
            <FontAwesomeIcon :icon="['fas', 'file']" size="3x" class="mb-8" />
            <div class="font-bold">{{ selectedFileRelation.file.extension }}</div>
            <div class="text-2xs max-w-full truncate break-all">
              {{ selectedFileRelation.file.originalName }}
            </div>
          </div>
        </div>

        <!-- Message if no file is selected -->
        <div v-if="!selectedFileRelation" class="w-full text-center select-none">
          <span>Geen bestand(en) geselecteerd</span>
        </div>

        <!-- Loader overlay -->
        <div
          v-if="isLoading && selectedFileRelation"
          class="surface absolute inset-0 flex h-full w-full flex-col items-center justify-center space-y-6"
        >
          <LoadingSpinner class="size-6" />
          <div
            v-if="showLongLoadingMessage"
            class="flex flex-col items-center justify-center space-y-2"
          >
            <span class="text-sm">Het laden duurt iets langer dan normaal…</span>
            <GeneralButton
              size="small"
              variant="outlined"
              :color="color"
              :icon="['fas', 'rotate-right']"
              title="Bestand opnieuw laden"
              @click="reloadFileRelation"
              >Opnieuw laden</GeneralButton
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type Component,
  type ComputedRef,
  type Ref,
  nextTick,
  shallowRef
} from "vue"
import { storeToRefs } from "pinia"
import FileBadges from "@/components/file/FileBadges.vue"
import FileViewerCrop from "@/components/file/FileViewerCrop.vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import LoadingSpinner from "@/components/loading/LoadingSpinner.vue"
import { FileRelation } from "@/models/file/file-relation.model"
import { useZoomImageWheel } from "@zoom-image/vue"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import { FileController } from "@/controllers/file-controller"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"
import { useWindowStore } from "@/store/windows.store"
import { isEqual } from "lodash-es"
import { useEventListener } from "@vueuse/core"
import { Capacitor } from "@capacitor/core"
import { FileOpener } from "@capacitor-community/file-opener"
import { FileTransfer } from "@capacitor/file-transfer"
import { Filesystem, Directory } from "@capacitor/filesystem"
import { useAlertStore } from "@/store/alert.store"
import { AlertTypes } from "@/interfaces/component/alert.interface"
import type { IWindow } from "@/interfaces/component/window.interface.ts"
import type { LayoutColumn } from "@/models/user_settings/layout-column.model"
import LayoutColumnHeader from "@/components/layout/LayoutColumnHeader.vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"

const modelValue = defineModel<FileRelation[]>({ default: () => [] })

const props = withDefaults(
  defineProps<{
    /**
     * Initially selected file relation. Changing this prop will change the selected file relation.
     */
    select?: FileRelation | null
    /**
     * Whether to automatically stretch the container to the bottom of the screen.
     */
    autoStretchToBottom?: boolean
    /**
     * Whether to hide the menu.
     */
    hideMenu?: boolean
    /**
     * The color of the file viewer.
     */
    color?: "primary-lighten-1" | "primary"
    /**
     * Get the array of file relations.
     */
    getArray?: () => FileRelation[]
    /**
     * Callback to invoke after a file is deleted.
     */
    onDelete?: () => void
    /**
     * Whether to display a deletion option.
     */
    allowDelete?: boolean
    deleteDisabledReason?: (fileRelation: FileRelation) => string | undefined
    /**
     * Whether the selected file relation may be modified by the current viewer context.
     */
    canModify?: (fileRelation: FileRelation) => boolean
    /**
     * When set, the file viewer renders this component instead of the normal file content.
     * Use `markRaw()` on the component to prevent Vue from making it reactive.
     */
    contentOverride?: Component
    /**
     * Title displayed in the simplified header when contentOverride is active.
     */
    contentOverrideTitle?: string
    /**
     * Props passed to the component rendered through contentOverride.
     */
    contentOverrideProps?: Record<string, unknown>
  }>(),
  {
    select: null,
    autoStretchToBottom: false,
    hideMenu: false,
    color: "primary-lighten-1",
    allowDelete: true,
    canModify: () => true,
    contentOverrideTitle: "",
    contentOverrideProps: () => ({})
  }
)

const emit = defineEmits(["loadingComplete"])

const onChildLoaded = inject("onChildLoaded", null) as (() => void) | null
const getDraggableWindow = inject("getDraggableWindow", null) as (() => HTMLDivElement) | null
const close = inject("close", null) as (() => void) | null

const layoutColumn = inject<ComputedRef<LayoutColumn> | null>("layoutColumn", null)

function clearContentOverride() {
  if (layoutColumn?.value?.props) {
    delete layoutColumn.value.props.contentOverride
    delete layoutColumn.value.props.contentOverrideTitle
    delete layoutColumn.value.props.contentOverrideProps
  }
}

const dialogStore = useDialogStore()
const { currentDialog } = storeToRefs(dialogStore)

const selectedFileRelation: Ref<FileRelation | null> = ref(null)

const canModifySelectedFile = computed(
  () => selectedFileRelation.value === null || props.canModify(selectedFileRelation.value)
)
const selectedDeleteDisabledReason = computed(() =>
  selectedFileRelation.value ? props.deleteDisabledReason?.(selectedFileRelation.value) : undefined
)

const maxImageHeight = computed(() => {
  return `${window.innerHeight - 200}px`
})

const isShowOriginalImage = ref(false)
const refreshKey = ref(Math.random())

const fileCurrentlyDisplayed = computed(() => {
  if (isShowOriginalImage.value) {
    return selectedFileRelation.value?.file
  }
  return selectedFileRelation.value?.file.getEffectiveFile()
})

const imgUrl = computed(() => {
  const file = fileCurrentlyDisplayed.value
  if (file?.url) {
    return appendQueryParams(file.url, { t: refreshKey.value })
  }
  return undefined
})

type DocumentViewerMode = "native" | "iframe"

const DOCUMENT_IFRAME_MAX_ATTEMPTS = 3
const LONG_LOADING_TIMEOUT_MS = 5000
const NATIVE_DOCUMENT_LOAD_TIMEOUT_MS = 2500
const IFRAME_DOCUMENT_LOAD_TIMEOUT_MS = 8000

const documentViewerMode = shallowRef<DocumentViewerMode>("native")
const documentIframeAttempt = shallowRef(0)
const documentViewerFailed = shallowRef(false)

let documentLoadTimeout: ReturnType<typeof setTimeout> | null = null
let documentLoadTimeoutToken = 0

function appendQueryParams(url: string, params: Record<string, string | number>) {
  const query = new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, String(value)])
  ).toString()
  const separator = url.includes("?") ? "&" : "?"
  return `${url}${separator}${query}`
}

const documentFile = computed(() => selectedFileRelation.value?.file)

const canUseNativeDocumentViewer = computed(() => {
  const file = documentFile.value
  return !!file?.isDocument() && file.isPdfDocument()
})

const canUseOfficeDocumentViewer = computed(() => {
  const file = documentFile.value
  return !!file?.isDocument() && (file.isWordDocument() || file.isSpreadsheet())
})

const nativeDocumentUrl = computed(() => {
  const file = documentFile.value
  return file?.url ? appendQueryParams(file.url, { t: refreshKey.value }) : undefined
})

const iframeDocumentFileUrl = computed(() => {
  const file = documentFile.value
  return file?.url ? appendQueryParams(file.url, { t: refreshKey.value }) : undefined
})

const nativeDocumentMimeType = computed(() => {
  if (documentFile.value?.isPdfDocument()) return "application/pdf"
  return undefined
})

const documentViewerKey = computed(() => {
  return [
    selectedFileRelation.value?.id ?? "none",
    documentViewerMode.value,
    documentIframeAttempt.value,
    refreshKey.value
  ].join(":")
})

const showNativeDocumentViewer = computed(() => {
  return (
    !documentViewerFailed.value &&
    documentViewerMode.value === "native" &&
    canUseNativeDocumentViewer.value &&
    !!nativeDocumentUrl.value
  )
})

const iframeUrl = computed(() => {
  const file = documentFile.value
  const fileUrl = iframeDocumentFileUrl.value

  if (!fileUrl || !file?.isDocument()) {
    return undefined
  }

  if (file.isPdfDocument()) {
    return `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`
  }

  if (!canUseOfficeDocumentViewer.value) {
    return undefined
  }

  const encodedFileUrl = encodeURIComponent(fileUrl)

  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedFileUrl}`
})

const showIframeDocumentViewer = computed(() => {
  return !documentViewerFailed.value && documentViewerMode.value === "iframe" && !!iframeUrl.value
})

function setFileRelations(fileRelations: FileRelation[], selected: FileRelation | null) {
  modelValue.value = fileRelations
  selectedFileRelation.value = selected
}

const isLoading = ref(true)
const isCropMode = ref(false)
const showLongLoadingMessage = ref(false)
let loadingTimeout: ReturnType<typeof setTimeout> | null = null

function startLoadingTimeout() {
  if (loadingTimeout) clearTimeout(loadingTimeout)
  showLongLoadingMessage.value = false
  loadingTimeout = setTimeout(() => {
    if (isLoading.value) {
      showLongLoadingMessage.value = true
    }
  }, LONG_LOADING_TIMEOUT_MS)
}

function clearLoadingTimeout() {
  if (loadingTimeout) clearTimeout(loadingTimeout)
  showLongLoadingMessage.value = false
}

function clearDocumentLoadTimeout() {
  if (documentLoadTimeout) {
    clearTimeout(documentLoadTimeout)
    documentLoadTimeout = null
  }
  documentLoadTimeoutToken++
}

function scheduleDocumentLoadTimeout() {
  clearDocumentLoadTimeout()
  startLoadingTimeout()
  const token = ++documentLoadTimeoutToken

  void nextTick(() => {
    if (
      token !== documentLoadTimeoutToken ||
      !selectedFileRelation.value?.file.isDocument() ||
      documentViewerFailed.value ||
      !isLoading.value
    ) {
      return
    }

    documentLoadTimeout = setTimeout(() => {
      if (token !== documentLoadTimeoutToken) return
      handleDocumentLoadFailure()
    }, getDocumentLoadTimeoutMs())
  })
}

function getDocumentLoadTimeoutMs() {
  if (documentViewerMode.value === "native") {
    return NATIVE_DOCUMENT_LOAD_TIMEOUT_MS
  }
  return IFRAME_DOCUMENT_LOAD_TIMEOUT_MS
}

function resetDocumentViewer() {
  clearDocumentLoadTimeout()
  documentViewerFailed.value = false
  documentIframeAttempt.value = 0

  if (!selectedFileRelation.value?.file.isDocument()) {
    documentViewerMode.value = "native"
    return
  }

  if (canUseNativeDocumentViewer.value) {
    documentViewerMode.value = "native"
  } else if (canUseOfficeDocumentViewer.value) {
    documentViewerMode.value = "iframe"
    documentIframeAttempt.value = 1
  } else {
    markDocumentViewerFailed()
    return
  }

  scheduleDocumentLoadTimeout()
}

function switchToIframeDocumentViewer() {
  if (!iframeUrl.value) {
    markDocumentViewerFailed()
    return
  }

  documentViewerMode.value = "iframe"
  documentIframeAttempt.value = 1
  scheduleDocumentLoadTimeout()
}

function retryIframeDocumentViewer() {
  if (documentIframeAttempt.value < DOCUMENT_IFRAME_MAX_ATTEMPTS) {
    documentIframeAttempt.value++
    scheduleDocumentLoadTimeout()
    return
  }

  markDocumentViewerFailed()
}

function handleDocumentLoadFailure() {
  if (documentViewerMode.value === "native") {
    switchToIframeDocumentViewer()
    return
  }

  retryIframeDocumentViewer()
}

function markDocumentViewerFailed() {
  clearDocumentLoadTimeout()
  clearLoadingTimeout()
  documentViewerFailed.value = true
  isLoading.value = false
}

async function reloadFileRelation() {
  const currentRel = selectedFileRelation.value
  if (!currentRel) return

  isLoading.value = true
  startLoadingTimeout()

  // Force toggle to reset DOM elements
  selectedFileRelation.value = null
  await nextTick()

  refreshKey.value++
  selectedFileRelation.value = currentRel
}

// Handle crop completion
async function onCropComplete(blob: Blob) {
  if (!selectedFileRelation.value || !canModifySelectedFile.value) {
    return
  }

  try {
    const modifiedFile = new File(
      [blob],
      selectedFileRelation.value.file.getOriginalFileNameIncludingExtension(),
      {
        type: "image/jpeg"
      }
    )
    await fileController.modifyFile(selectedFileRelation.value.file, modifiedFile)

    isCropMode.value = false
    isShowOriginalImage.value = false
    refreshKey.value++
  } catch (err) {
    console.error("Crop upload error:", err)
    alert("Fout bij uploaden van de afbeelding.")
  }
}

function onContentLoaded() {
  clearLoadingTimeout()
  // Adjust the draggable window dimensions if needed.
  if (getDraggableWindow) {
    const draggableWindow = getDraggableWindow()
    if (selectedFileRelation.value && draggableWindow) {
      if (selectedFileRelation.value?.file.isDocument()) {
        draggableWindow.classList.add("lg:w-[840px]")
        draggableWindow.style.height = maxImageHeight.value
      } else {
        draggableWindow.classList.add("lg:w-[40%]")
      }
    }
  }

  if (onChildLoaded) {
    onChildLoaded()
  }
  if (imageContainer.value) {
    createZoomImageWheel(imageContainer.value)
  }
  // Once loaded, hide the loader.
  isLoading.value = false
}

function onContentFailed() {
  console.error("Content failed to load")
  clearLoadingTimeout()
  isLoading.value = false
}

function onDocumentContentLoaded() {
  clearDocumentLoadTimeout()
  onContentLoaded()
}

function onNativeDocumentFailed() {
  switchToIframeDocumentViewer()
}

function onIframeDocumentLoaded() {
  // Iframe contents cannot be inspected reliably; load is the browser-provided success signal.
  onDocumentContentLoaded()
}

function onIframeDocumentFailed() {
  retryIframeDocumentViewer()
}

watch(selectedFileRelation, (newVal) => {
  if (newVal) {
    isLoading.value = !!(
      selectedFileRelation.value?.file.isImage() || selectedFileRelation.value?.file.isDocument()
    )
    if (isLoading.value) {
      startLoadingTimeout()
    }
    resetDocumentViewer()
  } else {
    clearLoadingTimeout()
    clearDocumentLoadTimeout()
    documentViewerFailed.value = false
    isLoading.value = false
  }
})

const imageContainer = ref<HTMLDivElement | null>(null)
const {
  createZoomImage: createZoomImageWheel,
  zoomImageState: zoomImageWheelState,
  setZoomImageState: setZoomImageWheelState
} = useZoomImageWheel()

watch(isShowOriginalImage, () => {
  // Reset rotation when switching between original and derived image.
  if (imageContainer.value) {
    setZoomImageWheelState({ currentRotation: 0 })
  }
})

const isRotatingFile = ref(false)
function rotateFile(orientation: string = "right") {
  if (isCropMode.value || !canModifySelectedFile.value) {
    // Rotating is not supported in crop mode.
    return
  }

  isRotatingFile.value = true

  const rotation = orientation === "left" ? -90 : 90
  const rotationDegrees = zoomImageWheelState.currentRotation + rotation
  setZoomImageWheelState({
    currentRotation: rotationDegrees
  })

  accumulatedDegreesAfterLastSync = (accumulatedDegreesAfterLastSync + rotation) % 360
  scheduleRotate()
}

let rotationSyncTimeout: any = null
let accumulatedDegreesAfterLastSync = 0

function scheduleRotate() {
  if (rotationSyncTimeout) {
    clearTimeout(rotationSyncTimeout)
  }
  rotationSyncTimeout = setTimeout(() => rotateAccumulated(), 2000)
}

const fileController: FileController = inject("fileController")!
async function rotateAccumulated() {
  if (accumulatedDegreesAfterLastSync !== 0) {
    await fileController.rotateFile(fileCurrentlyDisplayed.value!, accumulatedDegreesAfterLastSync)
    accumulatedDegreesAfterLastSync = 0
  }
  isRotatingFile.value = false
}

const thisWindow: Ref<IWindow> | undefined = inject("window", undefined)
async function removeFileRelation() {
  if (
    selectedFileRelation.value !== null &&
    canModifySelectedFile.value &&
    !selectedDeleteDisabledReason.value
  ) {
    const fileRelation = selectedFileRelation.value
    currentDialog.value = {
      title: "Bestand verwijderen",
      body: `Weet je zeker dat je ${selectedFileRelation.value.file.originalName} wilt verwijderen?`,
      type: DialogTypes.CONFIRM,
      onConfirm: async () => {
        // onDelete can be provided in the following ways (optional):
        // - directly to this component (see FileViewController.displayFileRelationsInColumn())
        // - by the parent window (by means of injection)
        const onDelete = props.onDelete ?? thisWindow?.value.props.onDelete

        if (props.deleteDisabledReason?.(fileRelation)) return
        try {
          await fileController.deleteFileRelation(fileRelation, onDelete)
        } catch {
          useAlertStore().addAlert({
            type: AlertTypes.ERROR,
            title: "Bestand niet verwijderd",
            body: "Het bestand kon niet worden verwijderd. Controleer of de akkoordkaart nog bewerkbaar is en probeer opnieuw."
          })
          return
        }
        const fileRelations = props.getArray?.() ?? modelValue.value
        const index = fileRelations.findIndex((relation) => relation.id === fileRelation.id)
        if (index !== -1) fileRelations.splice(index, 1)
        modelValue.value = fileRelations
        if (modelValue.value.length > 0) {
          cycleFileRelation()
        } else {
          selectedFileRelation.value = null
          if (close) {
            close()
          }
        }
      },
      textConfirm: "Verwijderen",
      classConfirm: "bg-error! text-on-error!"
    }
  }
}

const windowStore = useWindowStore()
const layoutColumnsStore = useLayoutColumnsStore()
const { fileViewers } = storeToRefs(layoutColumnsStore)
const { windows } = storeToRefs(windowStore)

function deselectFileRelation() {
  const fileIndex = fileViewers.value.findIndex((fileViewer) => {
    return isEqual(fileViewer.props?.modelValue, modelValue.value)
  })
  if (fileIndex !== -1) {
    if (fileViewers.value[fileIndex].props) {
      fileViewers.value[fileIndex].props.modelValue = null
      fileViewers.value[fileIndex].props.select = null
    }
  }
  const windowIndex = windows.value.findIndex((window) => {
    return isEqual(window.props?.modelValue, modelValue.value)
  })
  if (windowIndex !== -1) {
    windows.value[windowIndex].props.modelValue = null
    windows.value[windowIndex].props.select = null
  }
}

const fileIndex = ref(
  selectedFileRelation.value ? modelValue.value.indexOf(selectedFileRelation.value) : 0
)

async function cycleFileRelation(direction: string = "next") {
  // Reset states when cycling files
  isShowOriginalImage.value = false
  isCropMode.value = false
  setZoomImageWheelState({ currentRotation: 0 })

  if (rotationSyncTimeout) {
    clearTimeout(rotationSyncTimeout)
    rotateAccumulated()
  }
  if (!modelValue?.value || modelValue.value.length === 0) {
    return
  }

  isLoading.value = true
  startLoadingTimeout()
  await nextTick()

  if (direction === "next") {
    fileIndex.value = fileIndex.value < modelValue.value.length - 1 ? fileIndex.value + 1 : 0
  } else if (direction === "prev") {
    fileIndex.value = fileIndex.value > 0 ? fileIndex.value - 1 : modelValue.value.length - 1
  }
  selectedFileRelation.value = modelValue.value[fileIndex.value]
}

async function webDownload(url: string, name: string) {
  const blob = await fetch(url).then((res) => res.blob())
  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.download = name
  a.href = objUrl
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(objUrl)
}

async function openDownloadedFile(savePath: string) {
  const { uri } = await Filesystem.getUri({
    directory: Directory.Documents,
    path: savePath
  })

  await FileOpener.open({ filePath: uri })
}

async function downloadFile(fileRelation: FileRelation | null) {
  if (!fileRelation) return

  if (!Capacitor.isNativePlatform()) {
    return webDownload(fileRelation.file.url, fileRelation.file.originalName)
  }

  const savePath = `${fileRelation.file.originalName}.${fileRelation.file.extension.toLowerCase()}`

  const fileInfo = await Filesystem.getUri({
    directory: Directory.Documents,
    path: savePath
  })

  await FileTransfer.downloadFile({
    url: fileRelation.file.url,
    path: fileInfo.uri
  })

  const alertStore = useAlertStore()
  alertStore.addAlert({
    type: AlertTypes.SUCCESS,
    title: "Bestand gedownload",
    body: `Het bestand is opgeslagen in de ${Directory.Documents} map.`,
    lifetime: 10000,
    action: {
      label: "Openen",
      handler: async () => {
        try {
          await openDownloadedFile(savePath)
        } catch {
          alertStore.addAlert({
            type: AlertTypes.ERROR,
            title: "Bestand openen mislukt",
            body: "Het bestand kon niet worden geopend.",
            lifetime: 5000
          })
        }
      }
    }
  })
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "ArrowRight") {
    cycleFileRelation("next")
  } else if (event.key === "ArrowLeft") {
    cycleFileRelation("prev")
  }
}

watch(
  () => props.select,
  (val) => {
    selectedFileRelation.value = val
    fileIndex.value = selectedFileRelation.value
      ? modelValue.value.indexOf(selectedFileRelation.value)
      : 0
  },
  { immediate: true }
)

onMounted(() => {
  useEventListener("keydown", handleKeydown)

  if (
    (selectedFileRelation.value?.file.isDocument() || selectedFileRelation.value?.file.isEmail()) &&
    onChildLoaded
  ) {
    onChildLoaded()
  }
  emit("loadingComplete")
})

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown)
  clearDocumentLoadTimeout()
  clearLoadingTimeout()
  if (rotationSyncTimeout) {
    clearTimeout(rotationSyncTimeout)
    rotateAccumulated()
  }
})
</script>

<style lang="scss" scoped>
iframe,
object {
  .resize & {
    position: relative;
    height: revert-layer;
  }
}

.image-container {
  height: var(--imageContainerHeight);
  width: var(--imageContainerWidth);
}
</style>
